import { ChangeEvent, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { ShareReferenceLocationState } from '$types/LocationState';
import { Button } from 'primereact/button';
import { Divider } from 'primereact/divider';
import { IconField } from 'primereact/iconfield';
import { InputIcon } from 'primereact/inputicon';
import { InputText } from 'primereact/inputtext';
import { NameRegex, emailRegex } from '../../constants/regex';
import { useAppDispatch } from '../../hooks';
import useRegisterForm from '../../hooks/useRegisterForm';
import { ReactComponent as GoogleIcon } from '../../icons/GoogleColorIcon.svg';
import { ReactComponent as LinkedInIcon } from '../../icons/LinkedInBlueIcon.svg';
import {
  employerEmailAuthAction,
  employerGoogleAuthAction,
  employerLinkedInAuthAction,
} from '../../store/legacy/auth';
import ResendEmailModal from './ResendEmailModal/ResendEmailModal';

interface FormTypes {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  confirmPassword: string;
  companyName?: string;
  companyURL?: string;
  phone: string;
  residenceCountry?: string;
  platformReferral?: string;
}

const EmployerRegisterForm = () => {
  const {
    register,
    handleSubmit,
    formState: { errors },
    getValues,
    watch,
  } = useForm<FormTypes>({ mode: 'onChange' });
  const [t, i18next] = useTranslation('global');
  const dispatch = useAppDispatch();
  const [showModal, setShowModal] = useState(false);
  const navigate = useNavigate();
  const location = useLocation() as ShareReferenceLocationState;
  const passwordWatch = watch('password', '');
  const { isMobile, authState, passStrength, passwordValidator } =
    useRegisterForm();
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const [isConfirmPasswordVisible, setIsConfirmPasswordVisible] =
    useState(false);

  const passColors = [
    'white',
    '#f44336',
    'purple',
    'blueviolet',
    'cyan',
    '#05668D',
  ];

  useEffect(() => {
    if (authState.isLoggedIn) {
      navigate('/employer-dashboard');
    }
  }, [authState.isLoggedIn, navigate]);

  useEffect(() => {
    if (!authState.loading && authState.employer?.employerRegisterSuccess) {
      setShowModal(true);
    }
  }, [authState.loading, authState.employer?.employerRegisterSuccess]);

  // Form declaration
  const firstName = register('firstName', {
    required: t('register.nameInputValid').toString(),
    maxLength: {
      value: 100,
      message: t('register.invalidFirstNameInput').toString(),
    },
    pattern: {
      value: NameRegex,
      message: t('register.invalidFirstNameInput').toString(),
    },
  });
  const lastName = register('lastName', {
    required: t('register.lastNameInputValid').toString(),
    maxLength: {
      value: 100,
      message: t('register.invalidLastNameInput').toString(),
    },
    pattern: {
      value: NameRegex,
      message: t('register.invalidLastNameInput').toString(),
    },
  });
  const email = register('email', {
    required: t('register.emailInputValid').toString(),
    maxLength: {
      value: 100,
      message: t('register.invalidEmailInput').toString(),
    },
    pattern: {
      value: emailRegex,
      message: 'Invalid email',
    },
  });
  const password = register('password', {
    validate: {
      validPass: (val) =>
        passwordValidator(val) >= 1 || 'Must contain at least 6 characters',
    },
  });
  const confirmPassword = register('confirmPassword', {
    required: t('register.confirmPasswordValid').toString(),
    validate: {
      passwordEqual: (value) =>
        value === getValues().password || t('register.matchPass').toString(),
    },
  });

  const companyName = register('companyName', {
    required: t('register.companyRequired').toString(),
  });
  const companyURL = register('companyURL');

  const submitForm = (values: FormTypes) => {
    const { companyName: companyNameField } = values;
    const { companyURL: companyURLField } = values;
    if (companyNameField) {
      dispatch(
        employerEmailAuthAction(
          {
            firstName: values.firstName.trim().replace(/\s\s+/g, ' '),
            lastName: values.lastName.trim().replace(/\s\s+/g, ' '),
            email: values.email.toLowerCase().trim(),
            password: values.password,
            shareReferenceId: location?.state?.shareReferenceId,
            shareCandidatesRosterId: location?.state?.shareCandidatesIds,
          },
          {
            CompanyName: companyNameField.trim(),
            CompanyURL: companyURLField?.trim(),
          },
          i18next.language
        )
      );
    }
  };

  const onGoogleSignIn = () => {
    dispatch(
      employerGoogleAuthAction(
        {
          CompanyName: 'Google Sign in (No company name)',
          ShareReferenceId: location?.state?.shareReferenceId,
          shareCandidatesRosterId: location?.state?.shareCandidatesIds,
        },
        i18next.language
      )
    );
  };

  const onLinkedInSignIn = () => {
    dispatch(
      employerLinkedInAuthAction(
        {
          CompanyName: 'LinkedIn Sign in (No company name)',
          ShareReferenceId: location?.state?.shareReferenceId,
          shareCandidatesRosterId: location?.state?.shareCandidatesIds,
        },
        i18next.language
      )
    );
  };

  const closeModal = (): void => {
    setShowModal(false);
  };

  return (
    <>
      <ResendEmailModal
        showModal={showModal}
        closeModal={closeModal}
        isMobile={!!isMobile}
        authState={authState}
      />
      <form
        id="register-form"
        data-testid="register-form"
        className="flex flex-col gap-12"
        onSubmit={handleSubmit(submitForm)}
        style={{
          WebkitFilter:
            !isMobile && showModal ? 'blur(1.5px)' : '' /* Safari 6.0 - 9.0 */,
          filter: !isMobile && showModal ? 'blur(1.5px)' : '',
          zIndex: showModal && isMobile ? '-10' : '1',
        }}
      >
        <div className="flex flex-col gap-4">
          <div className="flex gap-4">
            <div className="w-full flex flex-col gap-1">
              <label
                htmlFor="firstName"
                className="text-spk-mediumContrast text-xs"
              >
                {t('register.nameInput')}
              </label>
              <IconField iconPosition="left" className="w-full">
                <InputIcon className="pi pi-user"> </InputIcon>
                <InputText
                  onChange={(e: ChangeEvent) => firstName.onChange(e)}
                  name={firstName.name}
                  onBlur={firstName.onBlur}
                  ref={firstName.ref}
                  type="text"
                  className={`w-full ${
                    errors.firstName?.message && 'border-spk-brightRed'
                  }`}
                />
              </IconField>
              {errors.firstName?.message && (
                <small data-testid="input-error" className="text-spk-brightRed">
                  {errors.firstName?.message}
                </small>
              )}
            </div>
            <div className="w-full flex flex-col gap-1">
              <label
                htmlFor="lastName"
                className="text-spk-mediumContrast text-xs"
              >
                {t('register.lastNameInput')}
              </label>
              <IconField iconPosition="left" className="w-full">
                <InputIcon className="pi pi-user"> </InputIcon>
                <InputText
                  onChange={(e: ChangeEvent) => lastName.onChange(e)}
                  name={lastName.name}
                  onBlur={lastName.onBlur}
                  ref={lastName.ref}
                  type="text"
                  className={`w-full ${
                    errors.lastName?.message && 'border-spk-brightRed'
                  }`}
                />
              </IconField>
              {errors.lastName?.message && (
                <small data-testid="input-error" className="text-spk-brightRed">
                  {errors.lastName?.message}
                </small>
              )}
            </div>
          </div>

          <div className="flex gap-4">
            <div className="w-full flex flex-col gap-1">
              <label
                htmlFor="email"
                className="text-spk-mediumContrast text-xs"
              >
                {t('register.emailInput')}
              </label>
              <IconField iconPosition="left" className="w-full">
                <InputIcon className="pi pi-envelope"> </InputIcon>
                <InputText
                  onChange={(e: ChangeEvent) => email.onChange(e)}
                  name={email.name}
                  onBlur={email.onBlur}
                  ref={email.ref}
                  type="text"
                  className={`w-full ${
                    errors.email?.message && 'border-spk-brightRed'
                  }`}
                />
              </IconField>
              {errors.email?.message && (
                <small data-testid="input-error" className="text-spk-brightRed">
                  {errors.email?.message}
                </small>
              )}
            </div>
            <div className="w-full flex flex-col gap-1">
              <label
                htmlFor="companyName"
                className="text-spk-mediumContrast text-xs"
              >
                {t('register.companyName')}
              </label>
              <IconField iconPosition="left" className="w-full">
                <InputIcon className="pi pi-building"> </InputIcon>
                <InputText
                  onChange={(e: ChangeEvent) => companyName.onChange(e)}
                  name={companyName.name}
                  onBlur={companyName.onBlur}
                  ref={companyName.ref}
                  type="text"
                  className={`w-full ${
                    errors.companyName?.message && 'border-spk-brightRed'
                  }`}
                />
              </IconField>
              {errors.companyName?.message && (
                <small data-testid="input-error" className="text-spk-brightRed">
                  {errors.companyName?.message}
                </small>
              )}
            </div>
          </div>
          <div className="w-full flex flex-col gap-1">
            <label
              htmlFor="companyURL"
              className="text-spk-mediumContrast text-xs"
            >
              {t('register.companyURL')}
            </label>
            <IconField iconPosition="left" className="w-full">
              <InputIcon className="pi pi-building"> </InputIcon>
              <InputText
                onChange={(e: ChangeEvent) => companyURL.onChange(e)}
                name={companyURL.name}
                onBlur={companyURL.onBlur}
                ref={companyURL.ref}
                type="text"
                className={`w-full ${
                  errors.companyURL?.message && 'border-spk-brightRed'
                }`}
              />
            </IconField>
          </div>

          <div className="w-full flex flex-col gap-1">
            <label
              htmlFor="password"
              className="text-spk-mediumContrast text-xs"
            >
              {t('register.passwordInput')}
            </label>
            <IconField
              iconPosition="left"
              className="w-full p-icon-field-right"
            >
              <InputIcon className="pi pi-key"> </InputIcon>
              <InputText
                onChange={(e: ChangeEvent) => password.onChange(e)}
                name={password.name}
                onBlur={password.onBlur}
                ref={password.ref}
                type={isPasswordVisible ? 'text' : 'password'}
                className={`w-full ${
                  errors.password?.message && 'border-spk-brightRed'
                }`}
              />
              <InputIcon
                onClick={() => setIsPasswordVisible(!isPasswordVisible)}
                className={`cursor-pointer pi ${
                  isPasswordVisible ? 'pi-eye-slash' : 'pi-eye'
                }`}
              />
            </IconField>
            {errors.password?.message && (
              <small data-testid="input-error" className="text-spk-brightRed">
                {errors.password?.message}
              </small>
            )}
          </div>
          <div className="w-full flex flex-col">
            <div className="w-full h-2.5 bg-spk-neutral-40 rounded-full">
              <div
                style={{
                  width: `${(passStrength / 4) * 100}%`,
                  background: passColors[passStrength],
                }}
                className="h-2.5 rounded-full"
              />
            </div>
            {passwordWatch.length >= 1 && (
              <p className="w-full text-spk-mediumContrast text-sm">
                {t('register.passStrength')}
                {t(`register.strength${passStrength}`)}
              </p>
            )}
          </div>

          <div className="w-full flex flex-col gap-1">
            <label
              htmlFor="confirmPassword"
              className="text-spk-mediumContrast text-xs"
            >
              {t('register.confirmPassword')}
            </label>
            <IconField
              iconPosition="left"
              className="w-full p-icon-field-right"
            >
              <InputIcon className="pi pi-key"> </InputIcon>
              <InputText
                onChange={(e: ChangeEvent) => confirmPassword.onChange(e)}
                name={confirmPassword.name}
                onBlur={confirmPassword.onBlur}
                ref={confirmPassword.ref}
                type={isConfirmPasswordVisible ? 'text' : 'password'}
                className={`w-full ${
                  errors.confirmPassword?.message && 'border-spk-brightRed'
                }`}
              />
              <InputIcon
                onClick={() =>
                  setIsConfirmPasswordVisible(!isConfirmPasswordVisible)
                }
                className={`cursor-pointer pi ${
                  isConfirmPasswordVisible ? 'pi-eye-slash' : 'pi-eye'
                }`}
              />
            </IconField>
            {errors.confirmPassword?.message && (
              <small data-testid="input-error" className="text-spk-brightRed">
                {errors.confirmPassword?.message}
              </small>
            )}
          </div>
          <div className="flex justify-start font-sm">
            <p className="text-spk-mediumContrast">
              {t('register.byCreating')}&nbsp;
            </p>
            <a
              href="https://info.simera.io/terms-of-service"
              target="_blank"
              rel="noreferrer"
              className="font-bold text-spk-brightBlue underline"
            >
              {t('register.TOS')}
            </a>
          </div>
        </div>
        <div>
          <Button
            severity="success"
            type="submit"
            onClick={handleSubmit(submitForm)}
            label={t('register.createAccBtn')}
            className="w-full"
          />
          <Divider align="center" className="py-8 m-0 ">
            <p className="text-xs font-normal">{t('common.or')}</p>
          </Divider>
          <div
            data-testid="login-buttons"
            className="w-full flex gap-4 justify-center"
          >
            <Button
              severity="secondary"
              className="w-fill-available flex gap-4"
              outlined
              style={{ border: '1px solid' }}
              onClick={onGoogleSignIn}
            >
              <GoogleIcon />
              <p>Google</p>
            </Button>
            <Button
              severity="secondary"
              className="w-fill-available flex gap-4"
              outlined
              style={{ border: '1px solid' }}
              onClick={onLinkedInSignIn}
            >
              <LinkedInIcon />
              <p>LinkedIn</p>
            </Button>
          </div>
        </div>
      </form>
    </>
  );
};

export default EmployerRegisterForm;
